import { useState, useMemo, Fragment } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Minus, Trash2, Repeat, ChevronDown, ChevronRight } from "lucide-react";

const PROPIOS_KEY = "__propios__";

function agrupar(items) {
  // Devuelve [{key, label, isPropios, items}] preservando orden de aparición.
  const orden = [];
  const map = new Map();
  for (const it of items) {
    const key = it.heredado_de || PROPIOS_KEY;
    if (!map.has(key)) {
      const isPropios = key === PROPIOS_KEY;
      map.set(key, { key, label: isPropios ? "Repuestos propios" : `Heredado de: ${it.heredado_de}`,
                     isPropios, items: [] });
      orden.push(key);
    }
    map.get(key).items.push(it);
  }
  return orden.map((k) => map.get(k));
}

export const TablaRepuestosCot = ({ items, onCambiarCantidad, onEliminar, tieneEquivalentes, onVerEquivalentes, aprobables = false, onAprobar }) => {
  const grupos = useMemo(() => agrupar(items), [items]);
  const [colapsados, setColapsados] = useState(new Set());
  const hayHerencias = grupos.some((g) => !g.isPropios);

  const toggle = (key) => setColapsados((s) => {
    const n = new Set(s);
    n.has(key) ? n.delete(key) : n.add(key);
    return n;
  });

  const eliminarGrupo = async (grupo) => {
    if (!window.confirm(`¿Eliminar los ${grupo.items.length} repuesto(s) del grupo "${grupo.label}"?`)) return;
    for (const it of grupo.items) await onEliminar(it.id);
  };

  return (
    <div className="bg-white border border-border rounded-sm overflow-x-auto print-area">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-zinc-900 text-white text-left">
            <th className="px-3 py-2 font-heading text-xs uppercase">Código de parte</th>
            <th className="px-3 py-2 font-heading text-xs uppercase">Nombre</th>
            <th className="px-3 py-2 font-heading text-xs uppercase text-center w-32">Cantidad</th>
            {aprobables && <th className="px-3 py-2 font-heading text-xs uppercase text-center w-24">Aprobado</th>}
            <th className="w-16 no-print"></th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr><td colSpan={aprobables ? 5 : 4} className="px-3 py-8 text-center text-muted-foreground text-sm">
              Sin repuestos. Carga una plantilla o busca repuestos del catálogo.
            </td></tr>
          )}
          {grupos.map((g) => {
            const oculto = colapsados.has(g.key);
            // Encabezado de grupo solo si hay alguna herencia (para no cambiar el look
            // cuando no hay herencias en la cotización).
            return (
              <Fragment key={g.key}>
                {hayHerencias && (
                  <tr data-testid={`grupo-header-${g.key}`}
                      className={`cot-group-header ${g.isPropios ? "bg-zinc-50" : "bg-amber-50"} border-t border-border`}>
                    <td colSpan={aprobables ? 5 : 4} className="px-3 py-1.5">
                      <div className="flex items-center gap-2">
                        <button className="no-print flex items-center gap-1 text-xs font-heading uppercase font-semibold"
                          data-testid={`toggle-grupo-${g.key}`} onClick={() => toggle(g.key)}>
                          {oculto ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
                          {g.label}
                          <span className="text-muted-foreground normal-case font-normal">({g.items.length})</span>
                        </button>
                        <span className="hidden print:inline text-xs font-heading uppercase font-semibold">{g.label} ({g.items.length})</span>
                        <div className="ml-auto no-print">
                          <Button data-testid={`eliminar-grupo-${g.key}`} variant="ghost" size="icon"
                            className="h-6 w-6 text-destructive" title="Eliminar todo el grupo"
                            onClick={() => eliminarGrupo(g)}>
                            <Trash2 size={12} />
                          </Button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                {g.items.map((it) => (
                  <tr key={it.id} data-testid={`cot-item-${it.codigo_parte}`}
                      className={`border-t border-border ${oculto ? "hidden cot-row-hidden" : ""}`}>
                    <td className="px-3 py-2 font-mono text-xs font-semibold">{it.codigo_parte}</td>
                    <td className="px-3 py-2">{it.nombre}
                      {it.heredado_de && (
                        <span className="ml-1.5 text-[9px] bg-amber-100 text-amber-800 px-1 py-0.5 rounded-sm uppercase font-semibold" title={`Heredado de: ${it.heredado_de}`}>
                          Heredado
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-center gap-1 no-print">
                        <Button data-testid={`menos-${it.codigo_parte}`} variant="outline" size="icon" className="h-6 w-6" onClick={() => onCambiarCantidad(it, -1)}><Minus size={12} /></Button>
                        <span className="w-8 text-center font-semibold" data-testid={`cantidad-${it.codigo_parte}`}>{it.cantidad}</span>
                        <Button data-testid={`mas-${it.codigo_parte}`} variant="outline" size="icon" className="h-6 w-6" onClick={() => onCambiarCantidad(it, 1)}><Plus size={12} /></Button>
                      </div>
                    </td>
                    {aprobables && (
                      <td className="px-3 py-2 text-center">
                        <Checkbox data-testid={`aprobar-item-${it.codigo_parte}`} checked={!!it.aprobado_cliente}
                          onCheckedChange={(v) => onAprobar?.(it, !!v)} />
                      </td>
                    )}
                    <td className="px-1 no-print">
                      <div className="flex gap-0.5">
                        {tieneEquivalentes?.(it) && (
                          <Button data-testid={`ver-equivalentes-${it.codigo_parte}`} variant="ghost" size="icon"
                            className="h-7 w-7 text-accent-foreground" title="Ver equivalentes"
                            onClick={() => onVerEquivalentes(it)}><Repeat size={13} /></Button>
                        )}
                        <Button data-testid={`eliminar-item-${it.codigo_parte}`} variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => onEliminar(it.id)}><Trash2 size={13} /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
